/* eslint-disable indent */

import { DefineOwnModule, RootMutations, RootActions } from 'stores/type';
import StorageManager, { StorageKey } from 'managers/storage.manager';
import { State, Getters, Actions, Mutations } from './types';

const initialState = {
  workspaceFinal: [],
  workspaceFinalPrevious: [],
  workspaceExhausted: [],
  workspaceExhaustedPrevious: [],

  needsGameCompletionAnimation: false,

  ...(StorageManager.load(StorageKey.WORKSPACE_INACTIVE) || {}),
};
// for backwards compatibility
if (initialState.hasOwnProperty('exhausted')) {
  delete (initialState as any).exhausted;
}

const moduleState: State = initialState;

type ExtraActions = Pick<RootActions, 'workspaceClear' | 'workspaceRevert' | 'historyIntegrity'>;
type ExtraMutations = Pick<RootMutations, 'DRAG_START' | 'RESET_PROGRESS' | 'LOGGED_IN'>;
const storeModule: DefineOwnModule<
  State,
  Getters,
  Mutations & ExtraMutations,
  Actions & ExtraActions
> = {
  state: moduleState,

  getters: {
    workspaceFinal: (state) => state.workspaceFinal,
    workspaceFinalPrevious: (state) => state.workspaceFinalPrevious,
    workspaceExhausted: (state) => state.workspaceExhausted,
    workspaceExhaustedPrevious: (state) => state.workspaceExhaustedPrevious,
    workspaceInactivePrevious: (state) =>
      state.workspaceExhaustedPrevious.concat(state.workspaceFinalPrevious),
    workspaceHasInactive: (_state, getters) =>
      !!getters.workspaceFinal.length || !!getters.workspaceExhausted.length,

    needsGameCompletionAnimation: (state) => state.needsGameCompletionAnimation,
  },

  actions: {
    async workspaceClear({ commit }) {
      commit('WORKSPACE_FINAL_CLEAR');
    },
    workspaceRevert({ commit }) {
      commit('WORKSPACE_FINAL_REVERT');
    },

    workspaceFinalAdd({ commit }, elements) {
      if (!elements.length) return;

      commit('WORKSPACE_FINAL_ADD', elements);
    },
    workspaceFinalRemove({ commit }, id) {
      commit('WORKSPACE_FINAL_REMOVE', id);
    },

    workspaceExhaustedAdd({ commit }, elements) {
      if (!elements.length) return;

      commit('WORKSPACE_EXHAUSTED_ADD', elements);
    },
    async workspaceExhaustedRemove({ commit }, id) {
      commit('WORKSPACE_EXHAUSTED_REMOVE', id);
    },

    historyIntegrity({ dispatch }) {
      dispatch('workspaceExhaustedCheck');
    },
    workspaceExhaustedCheck({ dispatch, getters }) {
      const elements = getters.workspaceElementsExhausted;
      dispatch('workspaceExhaustedAdd', elements);
      dispatch('workspaceRemoveExhausted');
    },
  },

  mutations: {
    WORKSPACE_FINAL_CLEAR(state) {
      if (state.workspaceFinal.length || state.workspaceExhausted.length) {
        state.needsGameCompletionAnimation = true;
      }

      state.workspaceExhaustedPrevious = state.workspaceExhausted;
      state.workspaceExhausted = [];

      state.workspaceFinalPrevious = state.workspaceFinal;
      state.workspaceFinal = [];
    },
    WORKSPACE_FINAL_REVERT(state) {
      state.workspaceExhausted = state.workspaceExhaustedPrevious;
      state.workspaceExhaustedPrevious = [];

      state.workspaceFinal = state.workspaceFinalPrevious;
      state.workspaceFinalPrevious = [];
    },
    DRAG_START(state) {
      if (state.workspaceExhaustedPrevious.length) {
        state.workspaceExhaustedPrevious = [];
      }

      if (state.workspaceFinalPrevious.length) {
        state.workspaceFinalPrevious = [];
      }
    },

    WORKSPACE_FINAL_ADD(state, elements) {
      state.workspaceFinal = state.workspaceFinal.concat(elements);
    },
    WORKSPACE_FINAL_REMOVE(state, id) {
      state.workspaceFinal = state.workspaceFinal.filter(({ elementId }) => id !== elementId);
      state.needsGameCompletionAnimation = true;
    },

    WORKSPACE_EXHAUSTED_ADD(state, elements) {
      state.workspaceExhausted = state.workspaceExhausted.concat(elements);
    },
    WORKSPACE_EXHAUSTED_REMOVE(state, id) {
      state.workspaceExhausted = state.workspaceExhausted.filter(({ elementId }) => id !== elementId);
      state.needsGameCompletionAnimation = true;
    },

    ANIMATION_EXHAUSTED_FINISH(state) {
      state.needsGameCompletionAnimation = true;
    },

    WORKSPACE_INACTIVE_RESET(state) {
      state.needsGameCompletionAnimation = false;
      state.workspaceFinalPrevious = [];
      state.workspaceExhaustedPrevious = [];
    },

    RESET_PROGRESS(state) {
      state.workspaceFinal = [];
      state.workspaceFinalPrevious = [];
      state.workspaceExhausted = [];
      state.workspaceExhaustedPrevious = [];
    },
    LOGGED_IN(state) {
      state.workspaceFinalPrevious = [];
      state.workspaceExhaustedPrevious = [];
    },
  },
};

export default storeModule;
